import { noop } from 'lodash';
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useContext,
} from 'react';
import type { UnsavedChangesProviderProps } from './UnsavedChangesProvider';

type UnsavedChangesContext = Required<
  Omit<
    UnsavedChangesProviderProps,
    'children' | 'dialogComponent' | 'isPageDirty' | 'shouldBlockNavigation'
  >
> & {
  /**
   * Indicates whether the dialog is being presented (presented when unsaved changes exist and the user has attempted to navigate away)
   */
  isDialogOpen: boolean;
  /**
   * Do unsaved changes exist?
   */
  isPageDirty: boolean;
  /**
   * The route to which the user is attempting to go to
   */
  nextRoute: string;
  /**
   * Function to invoke when Cancel button is clicked
   */
  onCancel: () => void;
  /**
   * Function to invoke when Continue button is clicked
   */
  onContinue: () => void;
  /**
   * Function to invoke to update the `isPageDirty` state
   */
  setIsPageDirty: Dispatch<SetStateAction<boolean>>;
  /**
   * Function to invoke to update the `nextRoute` state
   */
  setNextRoute: Dispatch<SetStateAction<string>>;
  /**
   * Navigate to a route without triggering the unsaved changes dialog.
   * Use this after a successful save when you need to redirect immediately.
   */
  navigateWithoutBlocking: (route: string) => Promise<void>;
};

const UNSAVED_CHANGES_CONTEXT_DEFAULTS = {
  cancelButtonText: '',
  dialogBody: '',
  dialogTitle: '',
  isDialogOpen: false,
  isPageDirty: false,
  nextRoute: '',
  onCancel: noop,
  onContinue: noop,
  setIsPageDirty: noop,
  setNextRoute: noop,
  submitButtonText: '',
  navigateWithoutBlocking: () => Promise.resolve(),
};

export const UnsavedChangesContext = createContext<UnsavedChangesContext>(
  UNSAVED_CHANGES_CONTEXT_DEFAULTS,
);

export function useUnsavedChangesContext() {
  return useContext(UnsavedChangesContext);
}
